import spinnerIcon from '@iconify/icons-material-symbols/progress-activity';
import he from 'he';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { Card } from '#pie/components/card/Card';
import { CopyButton } from '#pie/components/copy-button/CopyButton';
import {
  DescriptionList,
  DescriptionListItem,
} from '#pie/components/description-list/DescriptionList';
import { Icon } from '#pie/components/icon/Icon';
import { InfoTooltip } from '#pie/components/info-tooltip/InfoTooltip';
import { Message } from '#pie/components/message/Message';
import { Page, PageHeader } from '#pie/components/page/Page';
import { PageHead } from '#pie/components/page-head/PageHead';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import type {
  InvoiceContactDetailsDto,
  MarketRoleType,
} from '#pie/internal/api/pie-bff';
import { useGetInvoiceContacts } from '#pie/internal/api/pie-bff';
import { ErrorPage } from '../error-page/ErrorPage';

const decodeHtmlEntities = (text: string | null | undefined) => {
  return text ? he.decode(text) /* c8 ignore start */ : '' /* c8 ignore end */;
};

export const InvoiceContactRegisterDetail = () => {
  const { t } = useTranslation();
  const { t: tmp } = useTranslation('marketParty');
  const marketPartyId = useParams()['marketPartyId']!;
  const subUnitId = useParams()['subUnitId']!;
  const { data, isLoading } = useGetInvoiceContacts(marketPartyId, {
    mprIdOrganization: subUnitId,
  });
  const invoiceContacts = data || [];
  const isForeignParty = invoiceContacts.some(
    (contact) => contact.registrationNumberOrigin !== 'NL',
  );
  const orgInfo = invoiceContacts.find((info: InvoiceContactDetailsDto) =>
    info.subUnits?.some((subUnit) => subUnit.mrId === subUnitId),
  );
  const marketrole = tmp(
    `marketParty.marketRole.${
      orgInfo?.marketRole as MarketRoleType
    }_abbreviations`,
  );
  const marketPartyTitle = `${orgInfo?.statutoryName} (${marketrole})`;
  const statutoryNameValue = orgInfo?.statutoryName || '';
  const countryValue = orgInfo?.country || '';

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Icon
          role="progressbar"
          aria-label="loading"
          icon={spinnerIcon}
          size={48}
          className="animate-spin text-content-tertiary opacity-50"
        />
      </div>
    );
  }

  if (!data) {
    return (
      <ErrorPage
        heading={t(
          'invoice_contact_register_detail.error_page.not_found.title',
        )}
        message={t(
          'invoice_contact_register_detail.error_page.not_found.message',
        )}
      />
    );
  }

  return (
    <>
      <PageHead
        title={t('invoice_contact_register_detail.title', {
          marketParty: marketPartyTitle,
        })}
      />
      <Page>
        <PageHeader
          title={t('invoice_contact_register_detail.title', {
            marketParty: marketPartyTitle,
          })}
          backHref={'../'}
          previousText={t('invoice_contact_register_detail.button_back')}
        />

        {isForeignParty ? (
          <Stack gap="lg" className="mx-auto">
            <Message variant="error">
              <Text>
                {t(
                  'invoice_contact_register_detail.error_message_foreign_parties',
                )}
              </Text>
            </Message>
          </Stack>
        ) : (
          <Text className="mx-6 pr-10">{t('invoice_contact.information')}</Text>
        )}

        <div className="flex flex-col gap-10 px-0 py-6 md:flex-row">
          <Stack gap="lg" className="flex-[2]">
            {invoiceContacts.map((contact) => (
              <Card
                key={contact.mprId}
                size="md"
                heading={
                  <Stack direction="row" gap="xs" align="center">
                    <Text
                      as="h2"
                      variant="h6"
                      className="text-content-heading font-bold"
                    >
                      {t('common.subject')}: {contact.subjectDescription}
                    </Text>
                  </Stack>
                }
              >
                {isForeignParty ? (
                  <Text className="text-content-body">
                    {t(
                      'invoice_contact_register_detail.text_foreign_parties_before',
                    )}
                    <ContactPersonLink contact={contact} />
                    {t(
                      'invoice_contact_register_detail.text_foreign_parties_after',
                    )}
                  </Text>
                ) : (
                  <DescriptionList variant="grid">
                    <div className="flex flex-col gap-4">
                      <DescriptionListItem
                        direction="vertical"
                        title={
                          <CopyButton
                            as="span"
                            role="button"
                            content={`${contact.statutoryName}\n${
                              contact.addressExtensionDescription
                                ? `${t(
                                    'invoice_contact_detail.address_extension',
                                  )}${decodeHtmlEntities(
                                    contact.addressExtensionDescription,
                                  )}`
                                : ''
                            }\n${contact.streetName} ${contact.houseNumber}${
                              contact.houseNumberExtension || ''
                            }\n${contact.zipCode} ${contact.city}\n${
                              contact.country
                            }`}
                            aria-label={`${contact.statutoryName}, ${
                              contact.addressExtensionDescription
                                ? t(
                                    'invoice_contact_detail.address_extension',
                                  ) +
                                  decodeHtmlEntities(
                                    contact.addressExtensionDescription,
                                  )
                                : ''
                            }, ${contact.streetName} ${contact.houseNumber}${
                              contact.houseNumberExtension || ''
                            }, ${contact.zipCode} ${contact.city}, ${
                              contact.country
                            }`}
                          >
                            {t('common.invoice_contact.address')}
                          </CopyButton>
                        }
                      >
                        <Text>
                          <CopyButton
                            as="span"
                            role="button"
                            content={statutoryNameValue}
                            aria-label={statutoryNameValue}
                          >
                            {contact.statutoryName || '-'}
                          </CopyButton>
                        </Text>
                        {contact.addressExtensionDescription && (
                          <Text>
                            <CopyButton
                              as="span"
                              role="button"
                              content={`${t(
                                'invoice_contact_detail.address_extension',
                              )}${decodeHtmlEntities(
                                contact.addressExtensionDescription,
                              )}`}
                              aria-label={decodeHtmlEntities(
                                contact.addressExtensionDescription,
                              )}
                              test-id="address-extension"
                            >
                              {`${t(
                                'invoice_contact_detail.address_extension',
                              )}${decodeHtmlEntities(
                                contact.addressExtensionDescription,
                              )}`}
                            </CopyButton>
                          </Text>
                        )}
                        <Text>
                          <CopyButton
                            as="span"
                            role="button"
                            content={`${contact.streetName} ${
                              contact.houseNumber
                            }${contact.houseNumberExtension || ''}`}
                            aria-label={`${contact.streetName} ${
                              contact.houseNumber
                            }${contact.houseNumberExtension || ''}`}
                          >
                            {contact.streetName} {contact.houseNumber}
                            {contact.houseNumberExtension || ''}
                          </CopyButton>
                        </Text>
                        <Text>
                          <CopyButton
                            as="span"
                            role="button"
                            content={`${contact.zipCode} ${contact.city}`}
                            aria-label={`${contact.zipCode} ${contact.city}`}
                          >
                            {contact.zipCode} {contact.city}
                          </CopyButton>
                        </Text>
                        <Text>
                          <CopyButton
                            as="span"
                            role="button"
                            content={countryValue}
                            aria-label={countryValue}
                          >
                            {countryValue || '-'}
                          </CopyButton>
                        </Text>
                      </DescriptionListItem>
                    </div>
                    <div className="flex flex-col gap-4">
                      <DescriptionListItem
                        direction="vertical"
                        title={t('common.invoice_contact.email')}
                      >
                        <CopyButton
                          as="span"
                          role="button"
                          content={contact.electronicAddressPrimary || '-'}
                          aria-label={contact.electronicAddressPrimary || '-'}
                        >
                          {contact.electronicAddressPrimary || '-'}
                        </CopyButton>
                      </DescriptionListItem>
                      {(contact?.subjectDescription?.includes(
                        'Verrekenen meetfouten',
                      ) ||
                        contact?.subjectDescription?.includes(
                          'Verrekenproces Meetfouten',
                        )) && (
                        <DescriptionListItem
                          direction="vertical"
                          title={t('common.contactperson')}
                        >
                          <Stack direction="row" gap="xs" align="center">
                            <ContactPersonLink contact={contact} />
                            <InfoTooltip
                              className="text-content-heading"
                              aria-label={t(
                                'invoice_contact.contactperson.aria-label',
                              )}
                              size={16}
                            >
                              {t('invoice_contact.contactperson.tooltip')}
                            </InfoTooltip>
                          </Stack>
                        </DescriptionListItem>
                      )}
                    </div>
                  </DescriptionList>
                )}
              </Card>
            ))}
          </Stack>

          <aside className="mt-4 flex flex-1 flex-col gap-6">
            <Stack gap="sm">
              <Stack gap="sm" className="border-b border-border-divider">
                <Text as="h2" variant="h6" className="mb-4 max-h-14">
                  {t('invoice_contact.organisation_information.title')}
                </Text>
              </Stack>

              <DescriptionList className="flex flex-col">
                <DescriptionListItem
                  direction="vertical"
                  title={tmp('company.statutory_name')}
                >
                  {orgInfo?.statutoryName || '-'}
                </DescriptionListItem>
                <DescriptionListItem
                  direction="vertical"
                  title={tmp('company.kvk')}
                >
                  <CopyButton
                    as="span"
                    role="button"
                    content={orgInfo?.chamberOfCommerceNumber || '-'}
                    aria-label={orgInfo?.chamberOfCommerceNumber || '-'}
                  >
                    {orgInfo?.chamberOfCommerceNumber || '-'}
                  </CopyButton>
                </DescriptionListItem>
                <DescriptionListItem
                  direction="vertical"
                  title={t('common.marketparty')}
                >
                  {marketPartyTitle}
                </DescriptionListItem>
                <DescriptionListItem
                  direction="vertical"
                  title={t('common.market_role')}
                >
                  {`${tmp(
                    `marketParty.marketRole.${
                      orgInfo?.marketRole as MarketRoleType
                    }_abbreviations`,
                  )} (${tmp(
                    `marketParty.marketRole.${
                      orgInfo?.marketRole as MarketRoleType
                    }`,
                  )})`}
                </DescriptionListItem>
                <DescriptionListItem
                  direction="vertical"
                  title={t('common.ean13')}
                >
                  {orgInfo?.ean13}
                </DescriptionListItem>
              </DescriptionList>
              <Stack gap="sm" className="mt-4 border-b border-border-divider">
                <Text as="h2" variant="h6" className="mb-4 max-h-14">
                  {t('common.subregistration.title')}
                </Text>
              </Stack>
              <Stack gap="md" className="flex flex-col">
                {orgInfo?.subUnits?.map((subUnit) => (
                  <Text key={subUnit.mrId} variant="base">
                    {subUnit?.name}
                    <Text variant="sm">{subUnit?.eanCode}</Text>
                  </Text>
                ))}
              </Stack>
            </Stack>
          </aside>
        </div>
      </Page>
    </>
  );
};

const ContactPersonLink = ({
  contact,
}: {
  contact: InvoiceContactDetailsDto;
}) => {
  const { t } = useTranslation();

  return (
    <Link
      className={'text-action-primary-enabled link'}
      to={`/contactpersonenregister/contactgroep/${
        contact.subjectId
      }?filters=${btoa(
        JSON.stringify({ marketPartyIds: [contact.accountId] }),
      )}`}
      aria-label={t('common.contactperson')}
    >
      {contact?.subjectDescription}
    </Link>
  );
};
